"use client";

import { useEffect, useState } from "react";
import Image from "next/image";
import Link from "next/link";
import { SignedIn, SignedOut, useUser } from "@clerk/clerk-react";
import { BadgeCheck } from "lucide-react";
import * as api from "@/src/api/sdk.gen";

export type UiCourse = {
  title: string;
  description?: string | null;
  providerName: string;
  badgeText?: string | null;
  lastUpdatedText?: string | null;
  lifetimeAccessText: string;
  levelPricing?: Array<{ level: string; price: string }>;
  mockAverageRating: number;
  mockRatingCount: number;
  mockLearnerCount: number;
  heroHighlights: string[];
  learnItems: string[];
  levelModules?: Array<{ level: string; modules: string[] }>;
  moreCourses?: Array<{ slug: string; title: string; badge?: string | null; priceText?: string | null }>;
  requirements?: string[];
  longDescription?: string | null;
  outcomes?: string[];
  catalogSlug: string;
  /** Offer key that sells this course, when it is one of the courses for sale. */
  offerPlanKey?: string | null;
};

const UUID_PATTERN = /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i;

function useCourseAccess(offerPlanKey: string | null | undefined) {
  const { isSignedIn } = useUser();
  const [access, setAccess] = useState<{ has: boolean; planId: number | null } | "unknown">("unknown");

  useEffect(() => {
    if (!isSignedIn) {
      setAccess({ has: false, planId: null });
      return;
    }
    let cancelled = false;
    void (async () => {
      try {
        const { data } = await api.getEntryLevelPlansApiV1PaymentsEntryLevelPlansGet();
        if (cancelled || !data?.plans) return;
        const matchingPlan = data.plans.find(
          (p) =>
            p.key === offerPlanKey ||
            ((offerPlanKey === "agentic" || offerPlanKey === "professionals") &&
              (p.key === "agentic" || p.key === "professionals")),
        );
        setAccess({
          has: matchingPlan?.already_owned ?? false,
          planId: matchingPlan?.plan_id ?? null,
        });
      } catch {
        if (!cancelled) setAccess({ has: false, planId: null });
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [isSignedIn, offerPlanKey]);

  return access;
}

export function CatalogPriceCard({ course }: { course: UiCourse }) {
  const access = useCourseAccess(course.offerPlanKey);
  const planId = access !== "unknown" ? access.planId : null;

  const buyHref = planId
    ? `/checkout/${planId}`
    : UUID_PATTERN.test(course.catalogSlug)
      ? `/courses/${course.catalogSlug}`
      : "/courses";

  const buttonClass =
    "inline-flex h-11 w-full items-center justify-center rounded-md bg-slate-900 text-white text-sm font-bold hover:bg-slate-800 transition-colors";

  return (
    <div className="overflow-hidden rounded-xl border border-slate-200 bg-white text-slate-900 shadow-[0_18px_55px_rgba(15,23,42,0.20)]">
      <div className="relative aspect-video bg-slate-200">
        <Image
          src="/images/catalog2.png"
          alt="Course preview"
          fill
          className="object-cover"
          sizes="(min-width: 1024px) 360px, 100vw"
          priority
        />
        <div className="absolute inset-0 bg-gradient-to-t from-black/45 via-black/5 to-transparent" />

        <div className="absolute bottom-3 left-0 right-0 text-center text-sm font-semibold text-white drop-shadow-sm">
          Preview this course
        </div>
      </div>

      <div className="p-5">
        <div className="rounded-lg border border-slate-200 bg-slate-50 px-3.5 py-4 text-sm text-slate-800">
          <div className="flex gap-2.5">
            <BadgeCheck className="mt-0.5 h-4 w-4 text-slate-700" />
            <div className="min-w-0">
              <div className="font-semibold">
                {access !== "unknown" && access.has
                  ? "You already have access to this course."
                  : "$7 unlocks this course, every level included."}
              </div>
            </div>
          </div>
        </div>

        <div className="mt-4">
          <SignedOut>
            <Link href={`/sign-in?redirect_url=${encodeURIComponent(buyHref)}`} className={buttonClass}>
              Sign in to purchase
            </Link>
          </SignedOut>
          <SignedIn>
            {access !== "unknown" && access.has ? (
              <Link href="/courses" className={buttonClass}>
                Go to course
              </Link>
            ) : course.offerPlanKey && access === "unknown" ? (
              <span aria-busy="true" className={`${buttonClass} opacity-60 cursor-wait`}>
                Checking your access…
              </span>
            ) : (
              <Link href={buyHref} className={buttonClass}>
                Buy this course
              </Link>
            )}
          </SignedIn>
        </div>
      </div>
    </div>
  );
}
